import config from '../config';
import { buildLeadConvertBody } from './marketingApi';

/** Segment-flow / Call Manager booking lead sources (stored on customer.booking_leads.source). */
export const BOOKING_LEAD_SOURCE = {
  MANUAL: 'manual',
  MARKETING: 'marketing',
  CALL_MANAGER: 'call_manager',
};

export function normalizeLeadPhone(raw) {
  const digits = String(raw || '').replace(/\D/g, '');
  if (digits.length === 10) return digits;
  if (digits.length === 12 && digits.startsWith('91')) return digits.slice(2);
  return digits;
}

/** Pick phone from form state, selected customer, or raw search text. */
export function resolveLeadPhone({ formPhone, selectedUserPhone, searchQuery } = {}) {
  for (const raw of [formPhone, selectedUserPhone, searchQuery]) {
    const digits = String(raw || '').replace(/\D/g, '');
    if (digits.length >= 10) return digits;
  }
  return '';
}

/** Field-specific validation for manual lead creation forms. */
export function validateBookingLeadInputs({
  phone,
  leadType,
  contactBy,
  hasSearchText = false,
  hasSelectedCustomer = false,
  requireExistingCustomer = false,
  customerId = null,
}) {
  if (requireExistingCustomer && !hasSelectedCustomer) {
    return 'Select a customer from the search results (click their name). New customers cannot be created from here.';
  }
  if (requireExistingCustomer && hasSelectedCustomer && !customerId) {
    return 'Could not resolve CRM customer for the selected contact. Try selecting them again.';
  }
  const phoneDigits = String(phone || '').replace(/\D/g, '');
  if (!phoneDigits || phoneDigits.length < 10) {
    if (hasSearchText && !hasSelectedCustomer) {
      return 'Select a customer from the search results (click their name). Typing a phone number alone is not enough.';
    }
    return 'Customer phone number is required — search and select a customer';
  }
  const normalized = normalizeLeadPhone(phoneDigits);
  if (!normalized || normalized.length < 10) {
    return 'Enter a valid 10-digit Indian phone number';
  }
  if (!leadType) return 'Lead type is required';
  if (!contactBy) return 'Contact by date and time is required';
  return null;
}

export function apiErrorMessage(data, fallback) {
  const detail = data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((d) => d.msg || JSON.stringify(d)).join('; ');
  if (detail && typeof detail === 'object') return detail.message || JSON.stringify(detail);
  return fallback;
}

const CONVERT_PROFILE_KEYS = [
  'name', 'business_name', 'business_category', 'domain', 'matched_category',
  'address', 'city', 'pincode', 'preferred_language', 'rating', 'star',
];

/** Body shape matches LeadsPage handleCreateLead → POST /booking-leads. */
export function buildBookingLeadBody({
  phone,
  leadType,
  contactBy,
  source,
  customerId,
  draftBookingId,
  pickupTime,
  pickupAddressId,
  dropAddressIds,
  vehicleClassId,
  remarks,
  member,
}) {
  const phone_number = normalizeLeadPhone(phone);
  if (!phone_number || phone_number.length < 10) {
    throw new Error('Enter a valid 10-digit Indian phone number');
  }
  const body = {
    phone_number,
    lead_type: leadType,
    contact_by: contactBy,
    draft_booking_id: draftBookingId ? Number(draftBookingId) : undefined,
    pickup_time: pickupTime || undefined,
    pickup_address_id: pickupAddressId || undefined,
    drop_address_ids: dropAddressIds?.length ? dropAddressIds : undefined,
    vehicle_class_id: vehicleClassId ? Number(vehicleClassId) : undefined,
    remarks: remarks?.trim() ? remarks.trim() : undefined,
  };
  if (source) body.source = source;
  if (customerId) body.customer_id = Number(customerId);

  const existingCustomerId = customerId ?? member?.customer_id ?? member?.entity?.customer_id;
  if (existingCustomerId && !body.customer_id) {
    body.customer_id = Number(existingCustomerId);
  }

  const existingCustomerIdFromMember = member?.customer_id ?? member?.entity?.customer_id;
  if (member && !existingCustomerIdFromMember) {
    const convert = buildLeadConvertBody(member);
    if (convert?.lead_id || convert?.phone) {
      body.ensure_customer_from_marketing_lead = true;
      if (convert.lead_id) body.marketing_lead_id = convert.lead_id;
      CONVERT_PROFILE_KEYS.forEach((key) => {
        if (convert[key] !== undefined) body[key] = convert[key];
      });
    }
  }

  Object.keys(body).forEach((k) => {
    if (body[k] === undefined || Number.isNaN(body[k])) delete body[k];
  });
  return body;
}

/** POST /api/customer/booking-leads — same endpoint as Leads page New Lead. */
export async function createBookingLead(body, { apiBase = config.customerApiBase } = {}) {
  const headers = { 'Content-Type': 'application/json' };
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(`${apiBase}/booking-leads`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(apiErrorMessage(data, 'Failed to create booking lead'));
  return data;
}
